import React, { useEffect, useMemo, useState } from 'react';
import { FlexView, Icon, Image, Tag, Text } from '@goodfynd/ui';
import { observer } from 'mobx-react-lite';

import config from '../../../config';
import strings from '../../../config/strings';
import { useStores } from '../../../stores';
import { dateUtil, vendorUtil } from '../../../utils';
import LikeButton from '../../LikeButton';
import PickupTimeOptions from '../../PickupTimeOptions';
import ShareButton from '../../ShareButton';
import VendorSchedule from '../VendorSchedule';

import {
  StyledBlock,
  StyledContainer,
  StyledCta,
  StyledCtaContainer,
  StyledDescription,
  StyledName,
  StyledNextEventInfo,
} from './styles';
import type { VendorInfoProps } from './types';

function VendorInfo({
  className,
  data,
  hasPrivateEvents,
  renderProductTypes,
  showAllEvents,
  showLikes = true,
  updateCurrentPickupLocation,
  ...props
}: VendorInfoProps) {
  const {
    orderStore: { event, resetPickupOptions },
  } = useStores();
  const [viewPickupTimeOptions, togglePickupTimeOptions] = useState(false);
  const [viewSchedule, toggleSchedule] = useState(false);

  const publicEvents =
    data?.map?.schedule?.dayLocations?.filter((event) => !event?.isPrivate) ||
    [];

  const currentPickupLocations = useMemo(
    () =>
      vendorUtil.getPickupLocations(
        new Date(data.map.operatingDayLocation.end),
        data.willOpenLater || data.isOpen,
        data.map.schedule.dayLocations
      ),
    [data]
  );

  const scheduleLocations = useMemo(() => {
    return showAllEvents
      ? data.map.schedule.dayLocations
      : currentPickupLocations;
  }, [currentPickupLocations, data.map.schedule.dayLocations, showAllEvents]);
  const hasPickupLocations = currentPickupLocations.length > 0;
  const currentPickupLocation = useMemo(
    () =>
      scheduleLocations[0]
        ? scheduleLocations.find((entry) => entry.id === event?.scheduleId) ||
          scheduleLocations[0]
        : undefined,
    [event?.scheduleId, scheduleLocations]
  );

  useEffect(() => {
    if (currentPickupLocation?.pickupCapacity && updateCurrentPickupLocation) {
      updateCurrentPickupLocation(currentPickupLocation);
    }
  }, [currentPickupLocation, updateCurrentPickupLocation]);

  useEffect(() => {
    resetPickupOptions?.(data.id);
  }, [data.id, resetPickupOptions]);

  // Convert base64 data URL to object URL for opening in a new tab
  const qrCodeObjectUrl = useMemo(() => {
    if (!data.qrCodeImageSrc?.startsWith('data:')) {
      return undefined;
    }

    try {
      const [header, base64] = data.qrCodeImageSrc.split(',');
      const mime = header.match(/:(.*?);/)?.[1] || 'image/png';
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      return URL.createObjectURL(new Blob([bytes], { type: mime }));
    } catch {
      return undefined;
    }
  }, [data.qrCodeImageSrc]);

  const hasEvents = !!data.map.schedule.dayLocations[0];

  const productTypeNames = data.productTypes.map(
    (productType) => productType.label
  );
  const hasProductTypes = productTypeNames.length > 0;

  let location = data.city || '';
  if (location && data.state) {
    location += `, ${data.state}`;
  } else {
    location = data.state || '';
  }

  console.log('hasEvents', hasEvents);
  return (
    <FlexView className={className} direction="column" gap={0} width="100%">
      <StyledContainer {...props}>
        <FlexView direction="column" size={1}>
          <StyledName
            as="h1"
            css={{ alignItems: 'center', display: 'flex', gap: 4 }}
          >
            {data.isDeleted && <Icon name="hide" />}
            {data.uniqueName}
          </StyledName>

          {renderProductTypes?.(productTypeNames) ?? (
            <>
              <Text as="body2" tag="div" truncate={1}>
                <span>{location}</span>

                {location && hasProductTypes && (
                  <span>{config.unicode.centerDotPadded}</span>
                )}

                {hasProductTypes && (
                  <span>{productTypeNames.map((name) => name).join(', ')}</span>
                )}
              </Text>

              <FlexView>
                {[...data.ownerships].filter(Boolean).map((membership) => (
                  <Tag key={membership.id} label={membership.name} />
                ))}
              </FlexView>
            </>
          )}
        </FlexView>

        <StyledBlock>
          {showLikes && (
            <LikeButton
              count={data.likes}
              item={data}
              liked={data.liked}
              type="vendor"
            />
          )}

          <ShareButton
            text={`Check out ${data.uniqueName} on Goodfynd!`}
            title={data.uniqueName || data.name}
            url={data.shortUrl}
          />

          {!!qrCodeObjectUrl && (
            <a
              href={qrCodeObjectUrl}
              rel="noreferrer"
              target="_blank"
              title={`View ${data.uniqueName || 'Vendor'} QR code`}
            >
              <Image
                alt={`${data.uniqueName || 'Vendor'} QR code`}
                height={64}
                src={qrCodeObjectUrl}
                width={64}
              />
            </a>
          )}
        </StyledBlock>
      </StyledContainer>

      <StyledContainer>
        <StyledDescription tag="pre">{data.description}</StyledDescription>
      </StyledContainer>

      {updateCurrentPickupLocation ? (
        <StyledNextEventInfo
          location={currentPickupLocation?.location}
          subTitle={strings.labels.showingMenu}
          directions={
            currentPickupLocation?.location &&
            currentPickupLocation.lotId &&
            `/events/${currentPickupLocation.lotId}`
          }
          title={
            hasPrivateEvents
              ? strings.labels.pickupLocation
              : strings.labels.currentPickupLocation
          }
        >
          {currentPickupLocation && (
            <Icon
              name="clock"
              label={
                <Text>
                  {dateUtil.format(
                    new Date(currentPickupLocation?.start as Date),
                    config.dateFormats.weekDayMonthDay
                  )}
                  &nbsp;
                  {event?.pickupTime ? (
                    `@ ${dateUtil.getTimeDisplay(
                      new Date(event.pickupTime as Date)
                    )}`
                  ) : (
                    <span>
                      from&nbsp;
                      {dateUtil.getTimeDisplay(
                        new Date(currentPickupLocation?.start as Date)
                      )}
                      &nbsp;-&nbsp;
                      {dateUtil.getTimeDisplay(
                        new Date(currentPickupLocation?.end as Date)
                      )}
                      &nbsp;
                    </span>
                  )}
                  {currentPickupLocation?.isPrivate && <Tag label="PRIVATE" />}
                </Text>
              }
            />
          )}

          {hasEvents && (
            <StyledCtaContainer>
              {hasPickupLocations && (
                <StyledCta
                  onClick={() => togglePickupTimeOptions(true)}
                  size="small"
                >
                  {event?.pickupTime
                    ? strings.labels.changePickupTime
                    : strings.labels.choosePickupTime}
                </StyledCta>
              )}

              {!hasPrivateEvents && publicEvents?.length ? (
                <StyledCta
                  iconLeft="calendar"
                  onClick={() => toggleSchedule(true)}
                  rank="tertiary"
                  size="small"
                >
                  {strings.labels.viewFullSchedule}
                </StyledCta>
              ) : null}
            </StyledCtaContainer>
          )}
        </StyledNextEventInfo>
      ) : null}

      {viewPickupTimeOptions && (
        <PickupTimeOptions
          close={() => togglePickupTimeOptions(false)}
          onContinue={() => togglePickupTimeOptions(false)}
          open={viewPickupTimeOptions}
          schedule={currentPickupLocations}
          vendorId={data.nameId}
        />
      )}

      {hasEvents && viewSchedule && (
        <VendorSchedule
          close={() => toggleSchedule(false)}
          events={publicEvents}
          open={viewSchedule}
          vendor={data}
        />
      )}
    </FlexView>
  );
}

export default observer(VendorInfo);
